import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import { useRef, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../../../firebase';

export default function Hero() {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  
  // Firebase State
  const [heroData, setHeroData] = useState({
    title: 'GELECEĞİN YAPILARINI\nBUGÜNDEN İNŞA EDİYORUZ',
    subtitle: 'Modern mimari, üstün malzeme kalitesi ve konforlu yaşam alanlarıyla hayalinizdeki evi inşa ediyoruz.',
    bgUrl: '/hero-dark-premium.png'
  });

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end start"]
  });

  const y = useTransform(scrollYProgress, [0, 1], ["0%", "50%"]);
  const scrollOpacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);

  useEffect(() => {
    const unsub = onSnapshot(doc(db, 'pages', 'home'), (doc) => {
      if (doc.exists()) {
        const data = doc.data();
        
        let finalBgUrl = data.heroBgUrl;
        if (!finalBgUrl || finalBgUrl.includes('yapsat-site-7') || finalBgUrl.includes('hero-atlas') || finalBgUrl.includes('hero-dark')) {
           finalBgUrl = '/hero-esenyurt-1.png';
        }

        setHeroData({
          title: data.heroTitle || 'GELECEĞİN YAPILARINI\nBUGÜNDEN İNŞA EDİYORUZ',
          subtitle: data.heroSubtitle || 'Modern mimari, üstün malzeme kalitesi ve konforlu yaşam alanlarıyla hayalinizdeki evi inşa ediyoruz.',
          bgUrl: finalBgUrl
        });
      }
    });
    return () => unsub();
  }, []);

  // Slider Logic
  const sliderImages = [
    heroData.bgUrl,
    '/hero-esenyurt-2.png', // İkinci Esenyurt tarzı masif Residence Bloğu
    '/showcase-modern-1.png' // Ultra-Modern Cam Şaheser Binası
  ];

  useEffect(() => {
    if (sliderImages.length > 1) {
      const timer = setInterval(() => {
        setCurrentImageIndex((prev) => (prev + 1) % sliderImages.length);
      }, 6000);
      return () => clearInterval(timer);
    }
  }, [sliderImages.length]);

  const titleLines = heroData.title.split('\n');

  return (
    <div ref={containerRef} className="relative w-full h-screen min-h-[700px] overflow-hidden bg-zinc-950">
      {/* Fixed Background Wrapper with Crossfade Slider */}
      <motion.div 
        className="absolute inset-0 z-0 overflow-hidden"
        style={{ opacity: scrollOpacity, y }}
      >
        <AnimatePresence mode="popLayout" initial={false}>
          <motion.div 
            key={currentImageIndex}
            className="absolute inset-0 origin-center"
            initial={{ opacity: 0, scale: 1.1 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 1.5, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="absolute inset-0 bg-black/40 z-10" />
            <motion.img
              src={sliderImages[currentImageIndex]}
              alt={`Hero Background ${currentImageIndex}`}
              className="w-full h-full object-cover object-center"
              animate={{ scale: [1, 1.05, 1] }}
              transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
            />
          </motion.div>
        </AnimatePresence>
      </motion.div>

      {/* Ultra Subtle Top Gradient for Navbar Readability */}
      <div className="absolute inset-0 z-10 bg-gradient-to-b from-black/50 via-transparent to-transparent h-32" />

      {/* Animated Grid Pattern (Sweep Effect) */}
      <motion.div 
        className="absolute inset-0 z-10 opacity-[0.05]"
        style={{
          backgroundImage: 'linear-gradient(rgba(255,255,255,1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,1) 1px, transparent 1px)',
          backgroundSize: '4rem 4rem',
        }}
        animate={{ y: [0, 64] }}
        transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
      />

      {/* Ultra-Luxury Cinematic Dust Particles */}
      <div className="absolute inset-0 z-10 pointer-events-none overflow-hidden mix-blend-screen">
        {[...Array(40)].map((_, i) => (
          <motion.div
            key={`dust-${i}`}
            className="absolute rounded-full bg-amber-200/50"
            style={{
              width: Math.random() * 4 + 1 + 'px',
              height: Math.random() * 4 + 1 + 'px',
              left: Math.random() * 100 + '%',
              top: Math.random() * 100 + '%',
              filter: 'blur(1px)'
            }}
            animate={{
              y: [0, -250 - Math.random() * 150],
              x: [0, (Math.random() - 0.5) * 150],
              opacity: [0, Math.random() * 0.8 + 0.2, 0],
              scale: [0, Math.random() * 1.5 + 0.5, 0]
            }}
            transition={{
              duration: Math.random() * 10 + 10,
              repeat: Infinity,
              ease: "linear",
              delay: Math.random() * 10
            }}
          />
        ))}
      </div>

      {/* Main Content */}
      <div className="relative z-20 h-full flex flex-col justify-center px-6 md:px-16 lg:px-24 max-w-7xl mx-auto w-full">
        {/* Badge */}
        <motion.div
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 1, delay: 0.2, ease: "easeOut" }}
          className="flex items-center gap-3 mb-4"
        >
          <div className="w-8 md:w-12 h-px bg-red-500" />
          <span className="text-red-400 text-[10px] md:text-xs font-bold uppercase tracking-[0.3em] drop-shadow-md">
            Premium Residence • Satışlar Başladı
          </span>
        </motion.div>

        {/* Dynamic Staggered Headline */}
        <h1 className="text-white font-black leading-[1.1] tracking-tight mb-4 flex flex-col gap-1 md:gap-2"
            style={{ fontFamily: 'Raleway, sans-serif', fontSize: 'clamp(1.8rem, 4.5vw, 3.8rem)', textShadow: '0 3px 25px rgba(0,0,0,0.5)' }}
        >
          <AnimatePresence mode="popLayout">
            <motion.div key={titleLines[0]} initial={{ opacity: 0, y: 30, rotateX: 15 }} animate={{ opacity: 1, y: 0, rotateX: 0 }} transition={{ duration: 1, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}>
              {titleLines[0]}
            </motion.div>
            {titleLines.length > 1 && (
              <motion.div key={titleLines[1]} initial={{ opacity: 0, y: 30, rotateX: 15 }} animate={{ opacity: 1, y: 0, rotateX: 0 }} transition={{ duration: 1, delay: 0.6, ease: [0.16, 1, 0.3, 1] }}
                   className="text-transparent bg-clip-text bg-gradient-to-r from-white via-white/90 to-white/50"
                   style={{ WebkitTextStroke: '1px rgba(255,255,255,0.15)', textShadow: 'none' }}>
                {titleLines[1]}
              </motion.div>
            )}
          </AnimatePresence>
        </h1>

        {/* Subheadline with Blur Reveal */}
        <AnimatePresence mode="wait">
          <motion.p
            key={heroData.subtitle}
            initial={{ opacity: 0, filter: 'blur(8px)', y: 15 }}
            animate={{ opacity: 1, filter: 'blur(0px)', y: 0 }}
            transition={{ duration: 0.8, delay: 0.8, ease: "easeOut" }}
            className="text-white/80 text-sm md:text-base lg:text-lg max-w-xl font-medium leading-relaxed mb-8"
            style={{ textShadow: '0 2px 15px rgba(0,0,0,0.6)' }}
          >
            {heroData.subtitle}
          </motion.p>
        </AnimatePresence>

        {/* Dynamic CTA Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1, ease: "easeOut" }}
          className="flex flex-col sm:flex-row gap-4"
        >
          <motion.div whileHover={{ scale: 1.03, y: -2 }} whileTap={{ scale: 0.97 }} className="flex">
            <Link to="/request-form" className="group relative overflow-hidden bg-red-600 hover:bg-red-500 text-white px-6 py-3 font-bold text-[11px] md:text-xs uppercase tracking-[0.2em] rounded-xl transition-all duration-300 shadow-[0_5px_20px_-5px_rgba(220,38,38,0.7)] flex items-center justify-center gap-2">
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-[150%] group-hover:translate-x-[150%] transition-transform duration-700 ease-out" />
              <i className="ri-home-heart-line text-lg relative z-10" />
              <span className="relative z-10">Talep Formu</span>
            </Link>
          </motion.div>
          
          <motion.div whileHover={{ scale: 1.03, y: -2, backgroundColor: 'rgba(255,255,255,0.1)' }} whileTap={{ scale: 0.97 }} className="flex">
            <Link to="/floor-plans" className="group border border-white/30 hover:border-white/60 text-white px-6 py-3 font-bold text-[11px] md:text-xs uppercase tracking-[0.2em] rounded-xl transition-all duration-300 backdrop-blur-md flex items-center justify-center gap-2">
              <i className="ri-layout-3-line text-lg group-hover:rotate-12 transition-transform duration-300" />
              <span>Kat Planları</span>
            </Link>
          </motion.div>
        </motion.div>
      </div>

      {/* Re-Added Dynamic Slider Indicators */}
      <div className="absolute bottom-10 left-10 md:left-16 lg:left-24 flex gap-3 z-30">
        {sliderImages.map((_, idx) => (
          <button 
            key={idx}
            onClick={() => setCurrentImageIndex(idx)}
            className="group relative py-4"
          >
            <div className={`h-1.5 rounded-full transition-all duration-500 ${currentImageIndex === idx ? 'w-16 bg-red-600 shadow-[0_0_15px_rgba(220,38,38,0.8)]' : 'w-8 bg-white/30 group-hover:bg-white/60'}`} />
          </button>
        ))}
      </div>

      {/* Enhanced Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 2 }}
        className="absolute bottom-10 right-10 md:right-16 lg:right-24 flex flex-col items-center gap-3 z-20 cursor-pointer group"
        onClick={() => window.scrollBy({ top: window.innerHeight, behavior: 'smooth' })}
      >
        <span className="text-white/50 group-hover:text-white transition-colors text-xs font-bold uppercase tracking-[0.3em]">Keşfet</span>
        <div className="w-8 h-14 rounded-full border-2 border-white/30 group-hover:border-white transition-colors flex items-start justify-center p-2 backdrop-blur-md">
          <motion.div 
            animate={{ y: [0, 20, 0], scale: [1, 1.2, 1], opacity: [0.5, 1, 0.5] }}
            transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
            className="w-1.5 h-3 bg-red-500 rounded-full shadow-[0_0_10px_rgba(220,38,38,1)]"
          />
        </div>
      </motion.div>
    </div>
  );
}
